/**
 * Turn Color Integration
 *
 * Handles Turn Color code lookup and mapping for scanned book pages.
 * Extracted from scannedBookService.ts for modularity and testability.
 */

import { supabase } from '@/lib/supabase/client';
import { Sentry } from '@/lib/sentry';
import type { TurnColorMapping } from '@/lib/turn-color';
import type { PageWithTurnColor, ScannedBookPage } from './types';
import { getBookPage, getBookPages } from '../scannedBookService';

// ============================================================================
// Helpers (exported for testing)
// ============================================================================

/**
 * Tokenize text into words, preserving punctuation
 */
export function tokenizeText(text: string): string[] {
  // Split on whitespace, keep punctuation attached
  return text
    .split(/\s+/)
    .filter(w => w.length > 0)
    .map(w => w.toLowerCase().replace(/[^a-z'-]/g, ''));
}

/**
 * Look up Turn Color codes for an array of words
 */
export async function lookupTurnCodes(words: string[]): Promise<Map<string, { turnCode: string; skillId: string | null }>> {
  const uniqueWords = [...new Set(words.filter(w => w.length > 0))];

  if (uniqueWords.length === 0) {
    return new Map();
  }

  const { data, error } = await supabase
    .from('turn_color_words')
    .select('word, turn_code, skill_id')
    .in('word', uniqueWords);

  if (error) {
    console.error('Turn code lookup error:', error);
    Sentry.captureException(error instanceof Error ? error : new Error(String(error)), {
      tags: { flow: 'scan_pipeline', step: 'turn_code_lookup' },
    });
    return new Map();
  }

  const codeMap = new Map<string, { turnCode: string; skillId: string | null }>();
  (data || []).forEach(row => {
    codeMap.set(row.word.toLowerCase(), {
      turnCode: row.turn_code,
      skillId: row.skill_id || null,
    });
  });

  return codeMap;
}

// ============================================================================
// Public API
// ============================================================================

/**
 * Get page with Turn Color codes applied
 */
export async function getPageWithTurnColor(
  pageId: string
): Promise<PageWithTurnColor | null> {
  const page = await getBookPage(pageId);
  if (!page) return null;

  const rawWords = page.extracted_text.split(/\s+/).filter(w => w.length > 0);
  const cleanWords = rawWords.map(w => w.toLowerCase().replace(/[^a-z'-]/g, ''));
  const turnCodes = await lookupTurnCodes(cleanWords);

  const words = rawWords.map((word, i) => {
    const cleanWord = cleanWords[i];
    const lookup = turnCodes.get(cleanWord) || null;

    return {
      word,
      turnCode: lookup?.turnCode || null,
      inLexicon: lookup !== null,
    };
  });

  return {
    pageId: page.id,
    pageNumber: page.page_number,
    detectedPageNumber: page.detected_page_number,
    text: page.extracted_text,
    pageType: page.page_type,
    blocks: page.text_blocks,
    renderingHints: page.rendering_hints,
    layoutData: page.layout_data,
    hasIllustration: page.has_illustration,
    illustrationDescription: page.illustration_description,
    needsRescan: page.needs_rescan,
    processingStatus: page.processing_status,
    imageUrl: page.image_url,
    enhancedImageUrl: page.enhanced_image_url,
    words,
  };
}

/**
 * Get all pages for a book with Turn Color codes
 */
export async function getBookPagesWithTurnColor(
  bookId: string
): Promise<PageWithTurnColor[]> {
  const pages = await getBookPages(bookId);

  // Collect all words from all pages
  const allWords: string[] = [];
  pages.forEach(page => {
    const words = page.extracted_text
      .split(/\s+/)
      .map(w => w.toLowerCase().replace(/[^a-z'-]/g, ''))
      .filter(w => w.length > 0);
    allWords.push(...words);
  });

  // Batch lookup all Turn Color codes
  const turnCodes = await lookupTurnCodes(allWords);

  // Build result with all enhanced fields
  return pages.map(page => {
    const rawWords = page.extracted_text.split(/\s+/).filter(w => w.length > 0);
    const words = rawWords.map(word => {
      const cleanWord = word.toLowerCase().replace(/[^a-z'-]/g, '');
      const lookup = turnCodes.get(cleanWord) || null;

      return {
        word,
        turnCode: lookup?.turnCode || null,
        inLexicon: lookup !== null,
      };
    });

    return {
      pageId: page.id,
      pageNumber: page.page_number,
      detectedPageNumber: page.detected_page_number,
      text: page.extracted_text,
      pageType: page.page_type,
      blocks: page.text_blocks,
      renderingHints: page.rendering_hints,
      layoutData: page.layout_data,
      hasIllustration: page.has_illustration,
      illustrationDescription: page.illustration_description,
      needsRescan: page.needs_rescan,
      processingStatus: page.processing_status,
      imageUrl: page.image_url,
      enhancedImageUrl: page.enhanced_image_url,
      // v4.5: Consolidation flag
      isSuperseded: page.is_superseded || false,
      words,
    };
  });
}

/**
 * Get color mappings for rendering
 */
export async function getColorMappings(): Promise<TurnColorMapping[]> {
  const { data, error } = await supabase
    .from('turn_color_mappings')
    .select('color_name, hex_code, description');

  if (error) {
    console.error('Color mappings error:', error);
    Sentry.captureException(error instanceof Error ? error : new Error(String(error)), {
      tags: { flow: 'scan_pipeline', step: 'color_mappings' },
    });
    return [];
  }

  // Convert null to undefined for description
  return (data || []).map(d => ({
    color_name: d.color_name,
    hex_code: d.hex_code,
    description: d.description ?? undefined,
  }));
}
